import React, { Component } from 'react';
import { inject, observer} from 'mobx-react';
import LoadingOverlay from 'react-loading-overlay';
import Grid from '@material-ui/core/Grid';
import Hidden from '@material-ui/core/Hidden';

// Components
import LoadStationData from './LoadStationData';
import VarPopover from '../../../VarPopover';
import VarPicker from '../../../VarPicker';
import DisplayCharts from './DisplayCharts';
import DisplayTables from './DisplayTables';

var app;

@inject('store') @observer
class HistoricalView extends Component {

    constructor(props) {
        super(props);
        app = this.props.store.app;
        app.setToolName('livestock')
        this.year = new Date().getFullYear()
        this.doy = Math.round((Date.now() - Date.parse(new Date().getFullYear(), 0, 0)) / 86400000)
        this.state = {
          data: null,
          data_is_loading: false,
          timescale: 'hours',
          disabled: [],
        }
    }

    componentDidMount() {
        this.initStateForLoading()
        LoadStationData({
            sid:this.props.station
        })
          .then(response => {
            this.setState({
              data:response.data.data,
              data_is_loading:false,
            })
          });
    }

    componentDidUpdate(prevProps,prevState) {
        if (prevProps.station!==this.props.station) {
                this.initStateForLoading()
                LoadStationData({
                    sid:this.props.station
                })
                  .then(response => {
                    this.setState({
                      data:response.data.data,
                      data_is_loading:false,
                    })
                  });
        }
    }

    // temperature humidity index
    //THI from https://www.progressivedairy.com/topics/herd-health/how-do-i-determine-how-do-i-calculate-temperature-humidity-index-thi
    calc_thi = (t,rh) => {
        // t: temperature (degF)
        // rh: relative humidity (%)
        return t-(0.55-(0.55*rh/100.))*(t-58)
    }

    calc_cattle_breathing_rate = (t,rh,s,w) => {
        // t: temperature (degF)
        // rh: relative humidity (%)
        // s: solar radiation (Wm-2)
        // w: wind speed (mph)
        let breathing_rate = (2.83*t) + (0.58*rh) - (0.76*w) + (0.039*s) - 196.4
        breathing_rate = (breathing_rate<40) ? 40 : parseInt(breathing_rate,10)
        return breathing_rate
    }

    calc_hourly_indices = (d) => {
        // d: data from acis call
        let iday,ihr
        let t,rh,s,w
        let thi,cbr
        let oseries=[]
        let thi_array=[]
        let cbr_array=[]
        if (!d) {return oseries}
        for (iday=0; iday<d.length; iday++) {
            thi_array = []
            cbr_array = []
            for (ihr=0; ihr<d[iday][1].length; ihr++) {
                t = (d[iday][1][ihr]==='M') ? null : parseFloat(d[iday][1][ihr])
                rh = (d[iday][2][ihr]==='M') ? null : parseFloat(d[iday][2][ihr])
                s = (d[iday][3][ihr]==='M') ? null : parseFloat(d[iday][3][ihr])
                w = (d[iday][4][ihr]==='M') ? null : parseFloat(d[iday][4][ihr])
                thi = (t !== null && rh !== null) ? this.calc_thi(t,rh) : null
                cbr = (t !== null && rh !== null && s !== null && w !== null) ? this.calc_cattle_breathing_rate(t,rh,s,w) : null
                if (thi !== null) {thi_array.push(thi)}
                if (cbr !== null) {cbr_array.push(cbr)}
            }
            oseries.push({
              'date': d[iday][0],
              'thi': thi_array,
              'cbr': cbr_array,
            })
        }
        return oseries
    }

    get_heat_index_thresholds = (livestock_type) => {
        // for a given livestock type, return the index type used,
        // along with any important thresholds
        // 'cbr' is 'cattle breathing rate' in breaths per minute
        // 'thi' is 'temperature humidity index'
        if (livestock_type==='cattle') {
            return {'idx_type':'cbr','alert':[90,110],'danger':[110,130],'emergency':[130,1000000]}
        } else if (livestock_type==='cow') {
            return {'idx_type':'thi','mild_discomfort':[68,72],'discomfort':[72,75],'alert':[75,79],'danger':[79,84],'emergency':[84,1000000]}
        } else if (livestock_type==='biganimal') {
            return {'idx_type':'thi','moderate':[72,74],'severe':[74,78],'very severe':[78,1000000]}
        } else if (livestock_type==='smallanimal') {
            return {'idx_type':'thi','moderate':[82,84],'severe':[84,86],'very severe':[86,1000000]}
        } else {
            return {}
        }
    }

    calc_heat_index_frequencies_hourly = () => {
        // t: object containing category names and index thresholds
        // e.g. {'idx_type':'thi','alert':75,'danger':79,'emergency':84}
        let t = this.get_heat_index_thresholds(app.livestock_getLivestockType)
        // heatIdxHourly: hourly heat index values by day
        let heatIdxHourly = this.calc_hourly_indices(this.state.data)
        // construct list of all hours annually
        let iday,iyr
        let year=null
        let year_today
        let temp_array=[]
        let heatIdxHourly_byYear=[]
        for (iday=0; iday<heatIdxHourly.length; iday++) {
            year_today = heatIdxHourly[iday]['date'].split('-')[0]
            // if just starting the loop, assigned year
            if (!year) { year = year_today }

            if (year_today===year) {
                //same year - append arrays
                temp_array.push(...heatIdxHourly[iday][t['idx_type']])
            } else {
                // write array of hourly values to output array
                heatIdxHourly_byYear.push({'year':year, 'idx':temp_array})
                // reinit year and annual array of indices
                year = year_today
                temp_array=[]
                
            }
        }
        // final year: write array of hourly values to output array
        heatIdxHourly_byYear.push({'year':year, 'idx':temp_array})

        // determine frequency of occurrences above specified thresholds
        let prop,filteredArr,outObject
        let outArray=[]
        let missThresh = 240 // allowed number of hours to be missing in year
        //let validThresh = parseInt(365.*24.,10) - missThresh
        let validThresh
        for (iyr=0; iyr<heatIdxHourly_byYear.length; iyr++) {
            // object for this year. It will hold the year and all frequencies
            outObject = {'year':heatIdxHourly_byYear[iyr]['year']}
            // calculate validThresh. For current year, we test based on day of year, instead of full year
            validThresh = (this.year.toString()===outObject.year) ? parseInt(this.doy*24.,10)-missThresh : parseInt(365*24.,10)-missThresh
            // If there are not enough valid hours, set categories to null for year
            if (heatIdxHourly_byYear[iyr]['idx'].length < validThresh) {
                // Set all categories for this year to null
                for (prop in t) {
                    outObject[prop]=null
                }
            } else {
                //loop through all given thresholds
                for (prop in t) {
                    filteredArr = []
                    if (prop!=='idx_type' && Object.prototype.hasOwnProperty.call(t, prop)) {
                        // eslint-disable-next-line
                        filteredArr = heatIdxHourly_byYear[iyr]['idx'].filter(v => v>=t[prop][0] && v<t[prop][1])
                        outObject[prop] = filteredArr.length
                    }
                }
            }
            // output array, will contain frequencies for each year
            outArray.push(outObject)
        }

        return outArray
    }

    calc_heat_index_frequencies_daily = () => {
        // t: object containing category names and index thresholds
        // e.g. {'idx_type':'thi','alert':75,'danger':79,'emergency':84}
        let t = this.get_heat_index_thresholds(app.livestock_getLivestockType)
        // heatIdxHourly: hourly heat index values by day
        let heatIdxHourly = this.calc_hourly_indices(this.state.data)
        // construct an annual list of daily maximum indices.
        // This will be a list of daily values, and each value is the highest index for that day
        let iday,iyr
        let year=null
        let year_today
        let temp_array=[]
        let heatIdxDaily_byYear=[]
        for (iday=0; iday<heatIdxHourly.length; iday++) {
            year_today = heatIdxHourly[iday]['date'].split('-')[0]
            // if just starting the loop, assigned year
            if (!year) { year = year_today }
            //
            if (year_today===year) {
                //same year - append arrays
                temp_array.push(Math.max(...heatIdxHourly[iday][t['idx_type']]))
            } else {
                // write array of hourly values to output array
                heatIdxDaily_byYear.push({'year':year, 'idx':temp_array})
                // reinit year and annual array of indices
                year = year_today
                temp_array=[]
                
            }
        }
        // final year: write array of hourly values to output array
        heatIdxDaily_byYear.push({'year':year, 'idx':temp_array})

        // determine frequency of occurrences above specified thresholds
        let prop,filteredArr,outObject
        let outArray=[]
        let missThresh = 10 // allowed number of days to be missing in year
        //let validThresh = parseInt(365.*24.,10) - missThresh
        let validThresh
        for (iyr=0; iyr<heatIdxDaily_byYear.length; iyr++) {
            // object for this year. It will hold the year and all frequencies
            outObject = {'year':heatIdxDaily_byYear[iyr]['year']}
            // calculate validThresh. For current year, we test based on day of year, instead of full year
            validThresh = (this.year.toString()===outObject.year) ? parseInt(this.doy,10)-missThresh : 365-missThresh
            // If there are not enough valid days, set categories to null for year
            if (heatIdxDaily_byYear[iyr]['idx'].length < validThresh) {
                // Set all categories for this year to null
                for (prop in t) {
                    outObject[prop]=null
                }
            } else {
                //loop through all given thresholds
                for (prop in t) {
                    filteredArr = []
                    if (prop!=='idx_type' && Object.prototype.hasOwnProperty.call(t, prop)) {
                        // eslint-disable-next-line
                        filteredArr = heatIdxDaily_byYear[iyr]['idx'].filter(v => v>=t[prop][0] && v<t[prop][1])
                        outObject[prop] = filteredArr.length
                    }
                }
            }
            // output array, will contain frequencies for each year
            outArray.push(outObject)
        }

        return outArray
    }

    handleChangeTimescale = (t) => {
      this.setState({
        timescale: t
      })
    }

    handleClickLegend = (dataKey) => {
        // dataKey : key in legend of chart
        if (this.state.disabled.includes(dataKey)) {
          this.setState({
            disabled: this.state.disabled.filter(obj => obj !== dataKey)
          });
        } else {
          this.setState({ disabled: this.state.disabled.concat([dataKey]) });
        }
    }

    initStateForLoading = () => {
        this.setState({
          data_is_loading: true,
          data: null,
        })
    }

    dataIsLoading = () => {
        return this.state.data_is_loading
    }

    getIdxTypeLabel = (type) => {
        if (type==='cattle') {
            return 'Cattle'
        } else if (type==='cow') {
            return 'Dairy Cow'
        } else if (type==='biganimal') {
            return 'Large Animal'
        } else if (type==='smallanimal') {
            return 'Small Animal'
        } else {
            return ''
        }

    }

    getChartInfo = (type) => {
        if (type==='cattle') {
            return {
                'typeLabel':'Cattle',
                'dataInfo': [
                    {'key':'emergency','label':'Emergency','color':'#581845'},
                    {'key':'danger','label':'Danger','color':'#900C3F'},
                    {'key':'alert','label':'Alert','color':'#C70039'},
                ]
            }
        } else if (type==='cow') {
            return {
                'typeLabel':'Dairy Cow',
                'dataInfo': [
                    {'key':'emergency','label':'Emergency','color':'#581845'},
                    {'key':'danger','label':'Danger','color':'#900C3F'},
                    {'key':'alert','label':'Alert','color':'#C70039'},
                    {'key':'discomfort','label':'Discomfort','color':'#FF5733'},
                    {'key':'mild_discomfort','label':'Mild Discomfort','color':'#FFC300'},
                ]
            }
        } else if (type==='biganimal') {
            return {
                'typeLabel':'Large Animal',
                'dataInfo': [
                    {'key':'very severe','label':'Very Severe','color':'#581845'},
                    {'key':'severe','label':'Severe','color':'#900C3F'},
                    {'key':'moderate','label':'Moderate','color':'#C70039'},
                ]
            }
        } else if (type==='smallanimal') {
            return {
                'typeLabel':'Small Animal',
                'dataInfo': [
                    {'key':'very severe','label':'Very Severe','color':'#581845'},
                    {'key':'severe','label':'Severe','color':'#900C3F'},
                    {'key':'moderate','label':'Moderate','color':'#C70039'},
                ]
            }
        } else {
            return []
        }
    }

    render() {
        let display;
        if (this.props.outputtype==='chart') {
            display = <DisplayCharts
                        data={(this.state.timescale==='hours') ? this.calc_heat_index_frequencies_hourly() : this.calc_heat_index_frequencies_daily()}
                        stnName={this.props.stnname}
                        loading={this.state.data_is_loading}
                        chartTitle={'Extreme Heat Index Frequencies ('+this.getIdxTypeLabel(app.livestock_getLivestockType)+', '+this.state.timescale+')'}
                        chartInfo={this.getChartInfo(app.livestock_getLivestockType)}
                        disabled={this.state.disabled}
                        onClickLegend={this.handleClickLegend}
                        timescale={this.state.timescale}
                      />
        }
        if (this.props.outputtype==='table') {
            display = <DisplayTables
                        data={(this.state.timescale==='hours') ? this.calc_heat_index_frequencies_hourly() : this.calc_heat_index_frequencies_daily()}
                        stnName={this.props.stnname}
                        loading={this.state.data_is_loading}
                        tableTitle={'Extreme Heat Index Frequencies ('+this.getIdxTypeLabel(app.livestock_getLivestockType)+', '+this.state.timescale+')'}
                        tableInfo={this.getChartInfo(app.livestock_getLivestockType)}
                      />
        }

        const options = [{
            title: 'Heat Index',
            name: 'livestock',
            options: [
                { label: 'Cattle', value: 'cattle' },
                { label: 'Dairy Cow', value: 'cow' },
                { label: 'Large Animal', value: 'biganimal' },
                { label: 'Small Animal', value: 'smallanimal' }
            ],
            selected: app.livestock_livestockType,
            onChange: app.livestock_setLivestockTypeFromRadioGroup,
            type: 'radio'
        },{
          title: 'Timescale',
          name: 'timescale',
          options: [
              { label: 'Hours', value: 'hours' },
              { label: 'Days', value: 'days' }
          ],
          selected: this.state.timescale,
          onChange: this.handleChangeTimescale,
          type: 'radio'
        }];

        return (
            <Grid container direction="row" justifyContent="flex-start" alignItems="flex-start">
                <Hidden smDown>
                    <Grid item container className="nothing" direction="column" md={2}>
                        <Grid item>
                            <VarPicker options={options} />
                        </Grid>
                    </Grid>
                </Hidden>
                <Grid item container className="nothing" direction="column" xs={12} md={10}>
                    <Grid item container direction="row" justifyContent="flex-start" alignItems="flex-start" spacing={1}>
                        <Hidden mdUp>
                            <Grid item>
                                <VarPopover>
                                    <VarPicker options={options} />
                                </VarPopover>
                            </Grid>
                        </Hidden>
                    </Grid>
                    <Grid item>
                        <LoadingOverlay
                            active={this.dataIsLoading()}
                            spinner
                            background={'rgba(255,255,255,1.0)'}
                            color={'rgba(34,139,34,1.0)'}
                            spinnerSize={'10vw'}
                        >
                            {display}
                        </LoadingOverlay>
                    </Grid>
                </Grid>
            </Grid>
        );

    }
}

export default HistoricalView;



// WEBPACK FOOTER //
// ./src/components/tools/LivestockIdxToolNew/HistoricalView/index.js